/**
 * Asset paths.
 *
 * Every path here is *site-root-relative*. Resolve it through `assetUrl` before
 * use so the `/clients/<slug>` mount point is applied exactly once. Never
 * concatenate an asset path by hand in a component.
 */

import { withBasePath } from './app.js';

/** Applies the SPA base path to a root-relative asset path. */
export function assetUrl(path: string): string {
  if (/^(https?:)?\/\//.test(path) || path.startsWith('data:') || path.startsWith('blob:')) {
    return path;
  }
  return withBasePath(path);
}

export const brandAssets = {
  logoPrimary: '/assets/brand/logo-primary.png',
  logoLight: '/assets/brand/logo-light.svg',
  monogram: '/assets/brand/monogram.svg',
  favicon: '/assets/brand/favicon.svg',
  brandGuidelines: '/assets/brand/brand-guidelines.pdf',
} as const;

export const posterAssets = {
  masterHero: '/assets/posters/built-with-purpose-hallway-fallback.svg',
  purposeHallway: '/assets/posters/built-with-purpose-hallway-fallback.svg',
  /**
   * The pathway heroes reuse the environment renders rather than separate
   * art-directed stills. A poster that disagreed with the scene it fades into
   * would make the crossfade visible.
   */
  personalTraining: '/assets/posters/performance-studio-fallback.webp',
  boxing: '/assets/posters/boxing-gym-fallback.webp',
  /**
   * Opening frame of the training gym, rendered from the shipped GLB by
   * `pnpm 3d:render --poster`. Generated rather than art-directed separately, so
   * it matches the live scene's first frame exactly and the crossfade out of the
   * fallback is invisible.
   */
  boxingGym: '/assets/posters/boxing-gym-fallback.webp',
  /**
   * Opening frame of the performance studio used by `/personal-training`.
   * Produced from the shipped desktop GLB (see `pnpm 3d:optimise:pt`).
   */
  performanceStudio: '/assets/posters/performance-studio-fallback.webp',
  /** Motivational wall posters hung along the Iron Resolve Combat Fitness hallway. */
  hallwayWall: {
    makeMuscles: '/assets/posters/hallway/make-muscles.png',
    everydayWorkout: '/assets/posters/hallway/everyday-workout.png',
    noSacrifice: '/assets/posters/hallway/no-sacrifice.png',
    notorious: '/assets/posters/hallway/notorious.png',
  },
} as const;

export const modelAssets = {
  master: {
    performanceAxisDesktop: '/assets/3d/master/performance-axis-desktop.glb',
    performanceAxisMobile: '/assets/3d/master/performance-axis-mobile.glb',
    goalMarkers: '/assets/3d/master/goal-markers.glb',
    neutralStudioEnvironment: '/assets/3d/master/neutral-studio-environment.glb',
  },
  personalTraining: {
    performanceCoreDesktop: '/assets/3d/personal-training/performance-core-desktop.glb',
    performanceCoreMobile: '/assets/3d/personal-training/performance-core-mobile.glb',
    nutritionDial: '/assets/3d/personal-training/nutrition-dial.glb',
    mealPlate: '/assets/3d/personal-training/meal-plate.glb',
    weightPlate: '/assets/3d/personal-training/weight-plate.glb',
    barbell: '/assets/3d/personal-training/barbell.glb',
    barbellPlates: '/assets/3d/personal-training/barbell-plates.glb',
    cardioTurbine: '/assets/3d/personal-training/cardio-turbine.glb',
    runningTrack: '/assets/3d/personal-training/running-track.glb',
    heartRateRing: '/assets/3d/personal-training/heart-rate-ring.glb',
    resistanceBand: '/assets/3d/personal-training/resistance-band.glb',
    dumbbellPair: '/assets/3d/personal-training/dumbbell-pair.glb',
    trainingTablet: '/assets/3d/personal-training/training-tablet.glb',
    trainingJournal: '/assets/3d/personal-training/training-journal.glb',
    stopwatch: '/assets/3d/personal-training/stopwatch.glb',
    digitalScale: '/assets/3d/personal-training/digital-scale.glb',
    heartRateMonitor: '/assets/3d/personal-training/heart-rate-monitor.glb',
    /**
     * The Built by Design environment. Produced from `assets-source/pt_gym.glb`
     * by `pnpm 3d:optimise:pt`; see docs/3d/pt-gym-optimisation.json.
     */
    performanceStudioDesktop: '/assets/3d/personal-training/performance-studio-desktop.glb',
    performanceStudioMobile: '/assets/3d/personal-training/performance-studio-mobile.glb',
    performanceStudio: '/assets/3d/personal-training/performance-studio.glb',
  },
  boxing: {
    glovesDesktop: '/assets/3d/boxing/boxing-gloves-desktop.glb',
    glovesMobile: '/assets/3d/boxing/boxing-gloves-mobile.glb',
    boxingRing: '/assets/3d/boxing/boxing-ring.glb',
    ringRopes: '/assets/3d/boxing/ring-ropes.glb',
    cornerPosts: '/assets/3d/boxing/corner-posts.glb',
    heavyBag: '/assets/3d/boxing/heavy-bag.glb',
    heavyBagChain: '/assets/3d/boxing/heavy-bag-chain.glb',
    handWrap: '/assets/3d/boxing/hand-wrap.glb',
    roundTimer: '/assets/3d/boxing/round-timer.glb',
    focusMitts: '/assets/3d/boxing/focus-mitts.glb',
    skippingRope: '/assets/3d/boxing/skipping-rope.glb',
    medicineBall: '/assets/3d/boxing/medicine-ball.glb',
    boxingStool: '/assets/3d/boxing/boxing-stool.glb',
    waterBottle: '/assets/3d/boxing/water-bottle.glb',
    towel: '/assets/3d/boxing/towel.glb',
    dumbbell: '/assets/3d/boxing/dumbbell.glb',
    /**
     * The Built in Rounds environment. Produced from `assets-source/training_gym.glb`
     * by `pnpm 3d:optimise`; see docs/3d/training-gym-optimisation.json for the
     * measured before/after figures. Used only by `/combat`.
     */
    trainingGymDesktop: '/assets/3d/boxing/training-gym-desktop.glb',
    trainingGymMobile: '/assets/3d/boxing/training-gym-mobile.glb',
  },
} as const;

export const coachAssets = {
  hero: '/assets/coach/zach-fight-victory.jpg',
  fullBody: '/assets/coach/coach-full-body.webp',
  fullBodyTransparent: '/assets/coach/coach-full-body-transparent.webp',
  portrait: '/assets/coach/zach-gym-portrait.jpg',
  personalTraining: '/assets/coach/zach-gym-portrait.jpg',
  boxing: '/assets/coach/zach-fight-portrait.jpg',
  victory: '/assets/coach/zach-fight-victory.jpg',
  introductionVideo: '/assets/coach/zach-resolve-loop.mp4',
  personalTrainingLoop: '/assets/coach/coach-personal-training-loop.mp4',
  boxingLoop: '/assets/coach/coach-boxing-loop.mp4',
} as const;

export const socialAssets = {
  masterOpenGraph: '/assets/social/master-open-graph.webp',
  personalTrainingOpenGraph: '/assets/social/personal-training-open-graph.webp',
  boxingOpenGraph: '/assets/social/boxing-open-graph.webp',
} as const;

/** Directories that receive client-supplied media. */
export const mediaDirectories = {
  results: '/assets/results/',
  exerciseThumbnails: '/assets/exercises/thumbnails/',
  exerciseVideos: '/assets/exercises/videos/',
  boxingDrillThumbnails: '/assets/boxing-drills/thumbnails/',
  boxingDrillVideos: '/assets/boxing-drills/videos/',
  nutrition: '/assets/nutrition/',
} as const;

/**
 * Inline SVG data URI used whenever a raster asset is absent. It is a deliberate
 * graphite/steel gradient panel rather than a broken-image icon, so a missing
 * asset degrades to something that still reads as designed.
 */
export const NEUTRAL_POSTER_DATA_URI =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1600 900" role="presentation">
      <defs>
        <radialGradient id="g" cx="50%" cy="42%" r="72%">
          <stop offset="0%" stop-color="#2A3038"/>
          <stop offset="55%" stop-color="#14181D"/>
          <stop offset="100%" stop-color="#080A0D"/>
        </radialGradient>
        <linearGradient id="l" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stop-color="#F3F0E9" stop-opacity="0"/>
          <stop offset="50%" stop-color="#F3F0E9" stop-opacity="0.35"/>
          <stop offset="100%" stop-color="#F3F0E9" stop-opacity="0"/>
        </linearGradient>
      </defs>
      <rect width="1600" height="900" fill="url(#g)"/>
      <circle cx="800" cy="420" r="212" fill="none" stroke="#8D97A4" stroke-opacity="0.22" stroke-width="1.5"/>
      <circle cx="800" cy="420" r="268" fill="none" stroke="#8D97A4" stroke-opacity="0.12" stroke-width="1"/>
      <rect x="240" y="419" width="1120" height="1.5" fill="url(#l)"/>
    </svg>`,
  );

export const assets = {
  brand: brandAssets,
  posters: posterAssets,
  models: modelAssets,
  coach: coachAssets,
  social: socialAssets,
  mediaDirectories,
  neutralPoster: NEUTRAL_POSTER_DATA_URI,
} as const;
